import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Bars2Icon, PlusCircleIcon } from '@heroicons/react/24/solid';
import { OneLineInput } from '../⚛️atoms/OneLineInput';
import { TiptapEditor } from '../⚛️atoms/TiptapEditor';
import { useDebounce } from 'react-use';
import ReactDragListView from 'react-drag-listview';
import { Checkbox } from '../⚛️atoms/Checkbox';
import toast from 'react-hot-toast';
import { getDataAPI, patchDataAPI } from '../../Helpers/FetchAPI';
import { ONE_TOAST_ID } from '../../constant';
import { TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { MarkdownWrapper } from './MarkdownWrapper';
import useLockedTranslate from '../../Hooks/useLockedTranslate';
import { getTranslation, updateTranslation } from '../../utils/translationHelper';
import { TranslateButton } from '../atoms/translateButton';
import { cloneWorkbookV2Value } from './editorUtils';
import {
  buildWorkbookItemSavePayload,
  getItemByTemplateApiPath,
  getTemplateItemId,
  WORKBOOK_V2_ANSWER_SAVE_DEBOUNCE_MS
} from '../../utils/workbookV2';

export const WorkbookV2Checkbox = forwardRef(
  ({ item, updateItem, editing, editor, oldAnswer, editingLanguage, workbook, creditId, lessonId }, ref) => {
    const [answers, setAnswers] = useState(item.answers ?? []);
    const [isCorrect, setIsCorrect] = useState(true);
    const oldQuestionInitializedRef = useRef(false);
    const { lang } = useLockedTranslate(workbook);

    const listItems = item.options?.items ?? [];
    const optionTranslations = item.options?.translations ?? {};

    const updateEditorItem = (updates) => {
      const nextItem = cloneWorkbookV2Value(item);
      nextItem.text = updates.text ?? nextItem.text;
      nextItem.page_number = updates.page_number ?? nextItem.page_number;
      nextItem.old_question_id = updates.old_question_id ?? nextItem.old_question_id;
      nextItem.translations = updates.translations ?? nextItem.translations;
      nextItem.options = {
        ...(nextItem.options ?? {}),
        ...(updates.options ?? {})
      };
      updateItem(nextItem);
    };

    useEffect(() => {
      if (oldAnswer?.length) {
        setAnswers([...oldAnswer]);
      }
    }, [oldAnswer]);

    useDebounce(
      () => {
        if (!editing) {
          return;
        }

        if (!oldQuestionInitializedRef.current) {
          oldQuestionInitializedRef.current = true;
          return;
        }

        if (item.old_question_id) {
          toast.promise(
            getDataAPI(`/workbookItems/question/${item.old_question_id}`).then((response) => {
              const question = response.data.questions.find((currentQuestion) => currentQuestion._id === item.old_question_id);
              if (question.type === 'checkbox') {
                updateEditorItem({
                  text: question.title,
                  options: {
                    items: question.options.map((option) => ({ item: option.title, correct: false }))
                  }
                });
              }
              return question;
            }),
            {
              loading: 'Getting Old Question Data',
              success: (question) => `The old Question was a ${question.type}`,
              error: 'Error getting old question data'
            },
            { id: 'old-question' }
          );
        }
      },
      250,
      [editing, item.old_question_id]
    );

    useDebounce(
      () => {
        if (editor) {
          return;
        }
        toast.promise(
          patchDataAPI(
            getItemByTemplateApiPath(creditId, getTemplateItemId(item)),
            buildWorkbookItemSavePayload({ answers, lessonId })
          ),
          {
            loading: 'Saving...',
            success: 'Saved',
            error: 'Error saving'
          },
          { id: ONE_TOAST_ID }
        );
      },
      WORKBOOK_V2_ANSWER_SAVE_DEBOUNCE_MS,
      [answers, creditId, editor, item, lessonId]
    );

    useImperativeHandle(ref, () => ({
      checkAnswers() {
        if (!item.options?.required) {
          return true;
        }
        const hasAllCorrectAnswers = listItems.filter((entry) => entry.correct).every((entry) => answers.includes(entry.item));
        const hasIncorrectAnswers = listItems.filter((entry) => !entry.correct).some((entry) => answers.includes(entry.item));
        const hasRightCombo = !hasIncorrectAnswers && hasAllCorrectAnswers;
        setIsCorrect(hasRightCombo);
        return hasRightCombo;
      }
    }));

    const handleCheck = (checked, value) => {
      const nextAnswers = [...answers];

      if (checked && !nextAnswers.find((answer) => answer === value)) {
        nextAnswers.push(value);
      } else {
        const answerIndex = nextAnswers.indexOf(value);
        if (answerIndex > -1) {
          nextAnswers.splice(answerIndex, 1);
        }
      }

      setAnswers(nextAnswers);
    };

    const updateListItem = (value, index) => {
      updateEditorItem({
        options: {
          items: listItems.map((entry, entryIndex) => (entryIndex === index ? { ...entry, item: value } : entry))
        }
      });
    };

    const handleCorrectnessEdit = (checked, index) => {
      updateEditorItem({
        options: {
          items: listItems.map((entry, entryIndex) => (entryIndex === index ? { ...entry, correct: checked } : entry))
        }
      });
    };

    const dragProps = {
      onDragEnd(fromIndex, toIndex) {
        const nextItems = [...listItems];
        const movedItem = nextItems.splice(fromIndex, 1)[0];
        nextItems.splice(toIndex, 0, movedItem);
        updateEditorItem({ options: { items: nextItems } });
      },
      nodeSelector: 'div',
      handleSelector: 'svg'
    };

    return (
      <div className={`flex w-full items-center gap-2 ${editing && 'border-pdteal rounded-lg border-2 px-8 py-12'}`}>
        {!editing && (
          <div className="relative flex flex-col">
            <div
              className={`bg-pdred/80 absolute bottom-[4%] -left-5 w-1.5 ${isCorrect ? 'h-[10%] opacity-0' : 'h-[94%] opacity-100'} transition-all duration-300 ease-out`}
            >
              <XMarkIcon className="bg-pdred/80 absolute top-0 -left-7 size-7 rounded-l-md pl-0.5 text-white" />
            </div>
            <>
              <MarkdownWrapper>{lang === 'en' ? item.text : getTranslation(item.translations, lang, 'text', item.text)}</MarkdownWrapper>
              <ul>
                {listItems.map((checkboxItem, index) => (
                  <li key={index} className="my-2 flex items-center gap-3">
                    <Checkbox
                      label={
                        typeof checkboxItem === 'string'
                          ? checkboxItem
                          : lang === 'en'
                            ? checkboxItem.item
                            : getTranslation(optionTranslations, lang, checkboxItem.item, checkboxItem.item)
                      }
                      checked={answers.includes(checkboxItem.item)}
                      onChange={(checked) => handleCheck(checked, checkboxItem.item)}
                    />
                  </li>
                ))}
              </ul>
            </>
          </div>
        )}
        {editing && (
          <div className="grid w-full grid-cols-2 gap-8">
            <div className="relative">
              <p className="mb-2 text-xl/6 font-medium text-gray-900">List Title</p>
              <TiptapEditor content={item.text} onChange={(value) => updateEditorItem({ text: value })} />
              {editingLanguage && (
                <TranslateButton
                  text={item.text}
                  language={editingLanguage}
                  onResponse={(value) =>
                    updateEditorItem({
                      translations: updateTranslation(item.translations ?? {}, editingLanguage, 'text', value)
                    })
                  }
                />
              )}
            </div>
            {editingLanguage && (
              <div>
                <p className="mb-2 text-xl/6 font-medium text-gray-900">List Title ({editingLanguage})</p>
                <TiptapEditor
                  content={getTranslation(item.translations, editingLanguage, 'text')}
                  onChange={(value) =>
                    updateEditorItem({
                      translations: updateTranslation(item.translations ?? {}, editingLanguage, 'text', value)
                    })
                  }
                />
              </div>
            )}
            <div className="col-start-1 w-2/5">
              <OneLineInput
                value={item.page_number}
                label="Page Number"
                onChange={(value) => updateEditorItem({ page_number: value })}
              />
            </div>
            <OneLineInput
              value={item.old_question_id}
              label="Old Question ID"
              onChange={(value) => updateEditorItem({ old_question_id: value })}
            />
            <Checkbox
              label="Required"
              checked={item.options?.required ?? false}
              onChange={(value) => updateEditorItem({ options: { required: value } })}
            />
            <div className="col-span-2 flex w-full flex-col">
              <span className="text-2xl">Checkboxes:</span>
              <ReactDragListView {...dragProps}>
                {listItems.map((checkboxItem, index) => (
                  <div className="flex w-full items-center gap-8 pb-8" key={`${checkboxItem.item}-${index}`}>
                    <Bars2Icon className="size-10"></Bars2Icon>
                    <div className="relative w-full">
                      <OneLineInput value={checkboxItem.item} onChange={(value) => updateListItem(value, index)} />
                      {editingLanguage && (
                        <TranslateButton
                          text={checkboxItem.item}
                          language={editingLanguage}
                          onResponse={(value) =>
                            updateEditorItem({
                              options: {
                                translations: updateTranslation(optionTranslations, editingLanguage, checkboxItem.item, value)
                              }
                            })
                          }
                        />
                      )}
                    </div>
                    {editingLanguage && (
                      <OneLineInput
                        value={getTranslation(optionTranslations, editingLanguage, checkboxItem.item)}
                        onChange={(value) =>
                          updateEditorItem({
                            options: {
                              translations: updateTranslation(optionTranslations, editingLanguage, checkboxItem.item, value)
                            }
                          })
                        }
                        label={`Item ${index + 1} (${editingLanguage})`}
                      />
                    )}
                    <div className="w-1/5">
                      <Checkbox
                        label="Correct"
                        checked={checkboxItem.correct}
                        onChange={(checked) => handleCorrectnessEdit(checked, index)}
                      />
                    </div>
                    <TrashIcon
                      className="text-pdred hover size-15 cursor-pointer"
                      onClick={() =>
                        updateEditorItem({ options: { items: listItems.toSpliced(index, 1) } })
                      }
                    />
                  </div>
                ))}
              </ReactDragListView>
              <PlusCircleIcon
                className="text-pdteal size-12 self-center drop-shadow-md hover:cursor-pointer"
                onClick={() =>
                  updateEditorItem({
                    options: {
                      items: [...listItems, { item: '', correct: false }]
                    }
                  })
                }
              />
            </div>
          </div>
        )}
      </div>
    );
  }
);
